import React, { useState, useEffect } from 'react';
import { mobileContentAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiPlus, FiEdit, FiTrash2, FiUserCheck, FiUserX } from 'react-icons/fi';
import { Toast, useToast, ConfirmModal, useConfirm } from './shared/Feedback';
import './StudentAppAdmin.css';

const TABS = [
  { id: 'quest', label: 'Quest Map' },
  { id: 'survival', label: 'Survival Kit' },
  { id: 'opportunities', label: 'Special Opportunities' },
  { id: 'costliving', label: 'Cost of Living' },
  { id: 'delegation', label: 'Access Control', mobileAdminOnly: true },
];

const OPPORTUNITY_CATEGORIES = ['SCHOLARSHIP', 'DISCOUNT', 'PROGRAM', 'EVENT', 'JOB_ADVERT', 'OTHER'];
const OPPORTUNITY_CATEGORY_LABELS = {
  SCHOLARSHIP: 'Scholarship',
  DISCOUNT: 'Tuition Discount',
  PROGRAM: 'Special Program',
  EVENT: 'Event',
  JOB_ADVERT: 'Job Advert',
  OTHER: 'Other',
};


// Mirrors backend mobile_content/image_links.py: a Google Drive share link
// opens Drive's viewer page, not the image, so turn it into the direct image
// link (the backend also does this on save).
const DRIVE_ID_PATTERNS = [
  /drive\.google\.com\/file\/d\/([\w-]{10,})/,
  /drive\.google\.com\/(?:open|uc|thumbnail)\?(?:.*&)?id=([\w-]{10,})/,
  /docs\.google\.com\/uc\?(?:.*&)?id=([\w-]{10,})/,
  /lh3\.googleusercontent\.com\/d\/([\w-]{10,})/,
];
const directImageUrl = (url) => {
  const raw = (url || '').trim();
  for (const re of DRIVE_ID_PATTERNS) {
    const m = raw.match(re);
    if (m) {
      // Files shared before Google's 2021 security update need their resourcekey.
      const key = raw.match(/[?&]resourcekey=([\w-]+)/);
      // =w1200-rj: at most 1200px wide, served as a JPEG (a 2.9 MB PNG → ~0.5 MB).
      return `https://lh3.googleusercontent.com/d/${m[1]}=w1200-rj${key ? `?resourcekey=${key[1]}` : ''}`;
    }
  }
  return raw;
};

const ImagePreview = ({ url }) => {
  const [failed, setFailed] = useState(false);
  const src = directImageUrl(url);
  useEffect(() => setFailed(false), [src]);
  if (!src) return null;
  if (failed) {
    return (
      <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>
        Can&apos;t load this image. For a Google Drive file, set sharing to
        &quot;Anyone with the link&quot; (Share → General access).
      </div>
    );
  }
  return (
    <img src={src} alt="Preview" onError={() => setFailed(true)}
      style={{ display: 'block', marginTop: 8, maxWidth: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'cover' }} />
  );
};

const StudentAppAdmin = () => {
  const { isMobileContentAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('quest');
  const [loading, setLoading] = useState(true);
  const [questStages, setQuestStages] = useState([]);
  const [packingItems, setPackingItems] = useState([]);
  const [survivalConfig, setSurvivalConfig] = useState(null);
  const [specialOpportunities, setSpecialOpportunities] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [delegationUserId, setDelegationUserId] = useState('');
  // Cost of Living state
  const [costCities, setCostCities] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [costItems, setCostItems] = useState([]);
  const [costPrices, setCostPrices] = useState([]);
  const [costPriceEdits, setCostPriceEdits] = useState({});
  const [costSaving, setCostSaving] = useState(false);
  const [currencyEdit, setCurrencyEdit] = useState(null); // null = not editing
  const [currencySaving, setCurrencySaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { toast, showToast } = useToast();
  const { confirmState, requestConfirm, confirm, cancel } = useConfirm();

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [stagesRes, packingRes, configRes, oppsRes] = await Promise.all([
        mobileContentAPI.questStages.list().catch(() => ({ data: [] })),
        mobileContentAPI.packingItems.list().catch(() => ({ data: [] })),
        mobileContentAPI.survivalConfig.get().catch(() => ({ data: null })),
        mobileContentAPI.specialOpportunities.list().catch(() => ({ data: [] })),
      ]);
      setQuestStages(Array.isArray(stagesRes.data) ? stagesRes.data : stagesRes.data?.results || []);
      setPackingItems(Array.isArray(packingRes.data) ? packingRes.data : packingRes.data?.results || []);
      setSurvivalConfig(configRes.data);
      setSpecialOpportunities(Array.isArray(oppsRes.data) ? oppsRes.data : oppsRes.data?.results || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchDelegations = async () => {
    if (!isMobileContentAdmin()) return;
    try {
      const [delRes, usersRes] = await Promise.all([
        mobileContentAPI.delegations.list().catch(() => ({ data: [] })),
        usersAPI.list().catch(() => ({ data: [] })),
      ]);
      const dels = Array.isArray(delRes.data) ? delRes.data : delRes.data?.results || [];
      setDelegations(dels);
      const users = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.results || [];
      // Filter to non-Chairman/CEO/COO/CMO/STUDENT users who don't already have a delegation
      // (those roles already have permanent mobile-content access — see IsCeoOrCoo backend-side).
      const delegatedIds = new Set(dels.map(d => d.delegated_user));
      setTeamMembers(users.filter(u =>
        !['CHAIRMAN', 'CEO', 'COO', 'CMO', 'STUDENT'].includes(u.role?.toUpperCase()) && !delegatedIds.has(u.id)
      ));
    } catch (e) {
      console.error('Failed to load delegations:', e);
    }
  };

  const handleAddDelegation = async () => {
    if (!delegationUserId) return;
    try {
      await mobileContentAPI.delegations.create({ delegated_user: parseInt(delegationUserId, 10), is_active: true });
      setDelegationUserId('');
      fetchDelegations();
      showToast('Delegation added.');
    } catch (e) {
      showToast(e.response?.data?.delegated_user?.[0] || e.response?.data?.detail || 'Failed to add delegation', 'error');
    }
  };

  const handleToggleDelegation = async (id, currentActive) => {
    try {
      await mobileContentAPI.delegations.update(id, { is_active: !currentActive });
      fetchDelegations();
      showToast(currentActive ? 'Delegation deactivated.' : 'Delegation activated.');
    } catch (e) {
      showToast('Failed to update delegation', 'error');
    }
  };

  const handleRemoveDelegation = (id) => {
    requestConfirm(
      { title: 'Remove Delegation', message: 'Remove this delegation? The team member will lose delegated access.' },
      async () => {
        try {
          await mobileContentAPI.delegations.delete(id);
          fetchDelegations();
          showToast('Delegation removed.');
        } catch (e) {
          showToast('Failed to remove delegation', 'error');
        }
      }
    );
  };

  const fetchCostData = async () => {
    try {
      const [citiesRes, catsRes, itemsRes, pricesRes] = await Promise.all([
        mobileContentAPI.costCities.list().catch(() => ({ data: [] })),
        mobileContentAPI.costCategories.list().catch(() => ({ data: [] })),
        mobileContentAPI.costItems.list().catch(() => ({ data: [] })),
        mobileContentAPI.costPrices.list().catch(() => ({ data: [] })),
      ]);
      setCostCities(Array.isArray(citiesRes.data) ? citiesRes.data : citiesRes.data?.results || []);
      setCostCategories(Array.isArray(catsRes.data) ? catsRes.data : catsRes.data?.results || []);
      setCostItems(Array.isArray(itemsRes.data) ? itemsRes.data : itemsRes.data?.results || []);
      setCostPrices(Array.isArray(pricesRes.data) ? pricesRes.data : pricesRes.data?.results || []);
      setCostPriceEdits({});
    } catch (e) {
      console.error('Failed to load cost of living data:', e);
    }
  };

  const handleCurrencyEdit = () => {
    setCurrencyEdit({
      currency_from: survivalConfig?.currency_from || 'USD',
      currency_to:   survivalConfig?.currency_to   || 'TRY',
      rate:          survivalConfig?.rate           || 34,
    });
  };

  const handleCurrencySave = async () => {
    if (!currencyEdit) return;
    setCurrencySaving(true);
    try {
      const res = await mobileContentAPI.survivalConfig.update(currencyEdit);
      setSurvivalConfig(prev => ({ ...prev, ...res.data }));
      setCurrencyEdit(null);
      showToast('Exchange rate saved.');
    } catch (e) {
      console.error('Failed to save currency rate:', e);
      showToast('Failed to save. Please try again.', 'error');
    } finally {
      setCurrencySaving(false);
    }
  };

  const handleCostCityCreate = async () => {
    const country = prompt('Country:', 'Turkey');
    if (!country) return;
    const name = prompt('City name:');
    if (!name) return;
    try {
      await mobileContentAPI.costCities.create({ name, country, order: costCities.length, is_active: true });
      fetchCostData();
      showToast('City added.');
    } catch (e) { showToast('Failed to add city', 'error'); }
  };

  const handleCostCityDelete = (id) => {
    requestConfirm(
      { title: 'Delete City', message: 'Delete this city and all its prices? This cannot be undone.' },
      async () => {
        try {
          await mobileContentAPI.costCities.delete(id);
          fetchCostData();
          showToast('City deleted.');
        } catch (e) { showToast('Failed to delete city', 'error'); }
      }
    );
  };

  const handleCostCategoryCreate = () => {
    setModalType('cost_category');
    setEditingId(null);
    setForm({ name: '', question: '', emoji: '\u{1F4B0}', order: costCategories.length, input_type: 'TIER_SELECT', options_json: '["None","Cheap","Moderate","Expensive"]', is_active: true });
    setShowModal(true);
  };

  const handleCostCategoryEdit = (cat) => {
    setModalType('cost_category');
    setEditingId(cat.id);
    setForm({ ...cat, options_json: JSON.stringify(cat.options_json || []) });
    setShowModal(true);
  };

  const handleCostCategoryDelete = (id) => {
    requestConfirm(
      { title: 'Delete Category', message: 'Delete this category and all its items? This cannot be undone.' },
      async () => {
        try {
          await mobileContentAPI.costCategories.delete(id);
          fetchCostData();
          showToast('Category deleted.');
        } catch (e) { showToast('Failed to delete category', 'error'); }
      }
    );
  };

  const handleCostItemCreate = (categoryId) => {
    setModalType('cost_item');
    setEditingId(null);
    setForm({ category: categoryId, name: '', emoji: '\u{1F4CC}', order: costItems.filter(i => i.category === categoryId).length, is_active: true });
    setShowModal(true);
  };

  const handleCostItemEdit = async (item) => {
    const cat = costCategories.find(c => c.id === item.category);
    // SINGLE_CHOICE categories: each item IS the choice — price stored with tier='default'.
    // TIER_SELECT / FREQUENCY_SELECT: student picks a tier per item — use options as columns.
    const isSingleChoice = cat?.input_type === 'SINGLE_CHOICE';
    const tiers = isSingleChoice
      ? ['default']
      : (cat?.options_json?.length > 0
          ? cat.options_json.filter(t => t.toLowerCase() !== 'none')
          : ['default']);
    // Fetch prices for this specific item fresh from the server (avoids pagination gaps)
    let itemPrices = [];
    try {
      const res = await mobileContentAPI.costPrices.list({ item: item.id, page_size: 1000 });
      itemPrices = Array.isArray(res.data) ? res.data : res.data?.results || [];
    } catch (e) {
      console.error('Failed to load item prices:', e);
    }
    const priceFields = {};
    for (const city of costCities.filter(c => c.is_active)) {
      for (const tier of tiers) {
        const existing = itemPrices.find(p => p.city === city.id && p.tier === tier);
        priceFields[`price_${city.id}_${tier}`] = existing ? String(existing.weekly_cost_try) : '';
      }
    }
    setModalType('cost_item');
    setEditingId(item.id);
    setForm({ ...item, ...priceFields, _tiers: tiers, _isSingleChoice: isSingleChoice });
    setShowModal(true);
  };

  const handleCostItemDelete = (id) => {
    requestConfirm(
      { title: 'Delete Item', message: 'Delete this item and all its prices? This cannot be undone.' },
      async () => {
        try {
          await mobileContentAPI.costItems.delete(id);
          fetchCostData();
          showToast('Item deleted.');
        } catch (e) { showToast('Failed to delete item', 'error'); }
      }
    );
  };

  const getCostPriceKey = (cityId, itemId, tier) => `${cityId}-${itemId}-${tier}`;

  const getCostPrice = (cityId, itemId, tier) => {
    const key = getCostPriceKey(cityId, itemId, tier);
    if (costPriceEdits[key] !== undefined) return costPriceEdits[key];
    const existing = costPrices.find(p => p.city === cityId && p.item === itemId && p.tier === tier);
    return existing ? existing.weekly_cost_try : '';
  };

  const handleCostPriceChange = (cityId, itemId, tier, value) => {
    const key = getCostPriceKey(cityId, itemId, tier);
    setCostPriceEdits(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveCostPrices = async () => {
    const prices = Object.entries(costPriceEdits).map(([key, value]) => {
      const [cityId, itemId, tier] = key.split('-');
      return { city: parseInt(cityId), item: parseInt(itemId), tier, weekly_cost_try: value || '0' };
    }).filter(p => p.weekly_cost_try !== '');
    if (prices.length === 0) return;
    setCostSaving(true);
    try {
      await mobileContentAPI.costPrices.bulkUpdate(prices);
      fetchCostData();
      showToast('Prices saved.');
    } catch (e) {
      showToast('Failed to save prices', 'error');
    } finally {
      setCostSaving(false);
    }
  };

  useEffect(() => {
    fetchAll();
    fetchDelegations();
    fetchCostData();
  }, []);

  const openCreate = (type) => {
    setModalType(type);
    setEditingId(null);
    switch (type) {
      case 'quest':
        setForm({ order: questStages.length, label: '', emoji: '📍', badge: '', is_milestone: false });
        break;
      case 'packing':
        setForm({ label: '', order: packingItems.length, destination_hint: '', is_active: true });
        break;
      case 'survival':
        setForm(survivalConfig || {
          currency_from: 'USD', currency_to: 'TRY', rate: 34,
          fun_equivalent_label: 'Doner Kebabs', fun_equivalent_unit_price: 5,
          weather_city: 'Istanbul', weather_hint: '12°C – pack layers',
        });
        break;
      case 'opportunity':
        setForm({ title: '', description: '', university: '', country: 'Turkey', deadline: '', link: '', image_url: '', category: 'SCHOLARSHIP', order: specialOpportunities.length, is_active: true });
        break;
      default:
        setForm({});
    }
    setShowModal(true);
  };

  const openEdit = async (type, item) => {
    setModalType(type);
    setEditingId(item.id);
    setForm({ ...item });
    setShowModal(true);
  };

  const openSurvivalEdit = () => {
    setModalType('survival');
    setEditingId(null);
    setForm(survivalConfig || {
      currency_from: 'USD', currency_to: 'TRY', rate: 34,
      fun_equivalent_label: 'Doner Kebabs', fun_equivalent_unit_price: 5,
      weather_city: 'Istanbul', weather_hint: '12°C – pack layers',
    });
    setShowModal(true);
  };

  const handleDelete = (type, id) => {
    requestConfirm(
      { title: 'Delete Item', message: 'Delete this item? This cannot be undone.' },
      async () => {
        try {
          switch (type) {
            case 'quest': await mobileContentAPI.questStages.delete(id); break;
            case 'packing': await mobileContentAPI.packingItems.delete(id); break;
            case 'opportunity': await mobileContentAPI.specialOpportunities.delete(id); break;
            case 'cost_category': await mobileContentAPI.costCategories.delete(id); fetchCostData(); break;
            case 'cost_item': await mobileContentAPI.costItems.delete(id); fetchCostData(); break;
            default: break;
          }
          fetchAll();
          showToast('Deleted successfully.');
        } catch (e) {
          console.error(e);
          showToast('Delete failed', 'error');
        }
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.rate !== undefined) payload.rate = Number(payload.rate);
      if (payload.fun_equivalent_unit_price !== undefined) payload.fun_equivalent_unit_price = Number(payload.fun_equivalent_unit_price);

      switch (modalType) {
        case 'quest':
          if (editingId) await mobileContentAPI.questStages.update(editingId, payload);
          else await mobileContentAPI.questStages.create(payload);
          break;
        case 'packing':
          if (editingId) await mobileContentAPI.packingItems.update(editingId, payload);
          else await mobileContentAPI.packingItems.create(payload);
          break;
        case 'opportunity':
          if (editingId) await mobileContentAPI.specialOpportunities.update(editingId, payload);
          else await mobileContentAPI.specialOpportunities.create(payload);
          break;
        case 'survival':
          await mobileContentAPI.survivalConfig.update(payload);
          break;
        case 'cost_category':
          if (typeof payload.options_json === 'string') {
            try { payload.options_json = JSON.parse(payload.options_json); } catch { payload.options_json = []; }
          }
          if (editingId) await mobileContentAPI.costCategories.update(editingId, payload);
          else await mobileContentAPI.costCategories.create(payload);
          fetchCostData();
          break;
        case 'cost_item': {
          // Separate price fields from item fields
          const priceUpdates = [];
          const itemPayload = {};
          const tiers = payload._tiers || [];
          // Key format: "price_{cityId}_{tier}" — use indexOf to split correctly
          // so tier names with underscores or spaces are preserved.
          for (const [key, value] of Object.entries(payload)) {
            if (key.startsWith('price_')) {
              const secondUnderscore = key.indexOf('_', 6); // skip "price_"
              const cityId = parseInt(key.slice(6, secondUnderscore), 10);
              const tier = key.slice(secondUnderscore + 1);
              if (value !== '' && value !== undefined && !isNaN(cityId)) {
                priceUpdates.push({ city: cityId, item: editingId || 0, tier, weekly_cost_try: value || '0' });
              }
            } else if (key !== '_tiers' && key !== '_isSingleChoice') {
              itemPayload[key] = value;
            }
          }
          let savedItem;
          if (editingId) {
            await mobileContentAPI.costItems.update(editingId, itemPayload);
            savedItem = editingId;
          } else {
            const res = await mobileContentAPI.costItems.create(itemPayload);
            savedItem = res.data?.id;
          }
          // Save prices if any were edited and we have an item ID
          if (priceUpdates.length > 0 && savedItem) {
            const fixedPrices = priceUpdates.map(p => ({ ...p, item: savedItem }));
            await mobileContentAPI.costPrices.bulkUpdate(fixedPrices);
          }
          fetchCostData();
          break;
        }
        default:
          break;
      }
      setShowModal(false);
      fetchAll();
      showToast(editingId ? 'Changes saved.' : 'Created successfully.');
    } catch (err) {
      console.error(err);
      showToast(err.response?.data ? JSON.stringify(err.response.data) : 'Save failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && questStages.length === 0) {
    return <div className="student-app-admin"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="student-app-admin">
      {toast && <Toast msg={toast.msg} type={toast.type} />}
      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger !== false}
          loading={confirmState.loading}
          onConfirm={confirm}
          onCancel={cancel}
        />
      )}
      <h1>Student App Admin</h1>
      <p className="student-app-admin-intro">
        Manage content for the student (mobile) app. Changes here appear in Quest Map, Survival Kit, Cost of Living, and Special Opportunities.
      </p>

      <div className="student-app-admin-tabs">
        {TABS.filter(t => !t.mobileAdminOnly || isMobileContentAdmin()).map((t) => (
          <button
            key={t.id}
            className={`tab-btn ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="student-app-admin-content">
        {activeTab === 'quest' && (
          <section className="admin-section">
            <div className="section-header">
              <h2>Quest Map Stages</h2>
              <button className="btn-primary" onClick={() => openCreate('quest')}><FiPlus /> Add Stage</button>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Order</th><th>Label</th><th>Emoji</th><th>Badge</th><th>Milestone</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {questStages.length === 0 ? (
                    <tr><td colSpan={6} className="empty-state">No stages. Add one to show in the app.</td></tr>
                  ) : (
                    questStages.map((s) => (
                      <tr key={s.id}>
                        <td>{s.order}</td>
                        <td>{s.label}</td>
                        <td>{s.emoji}</td>
                        <td>{s.badge || '—'}</td>
                        <td>{s.is_milestone ? 'Yes' : 'No'}</td>
                        <td>
                          <button className="btn-icon" onClick={() => openEdit('quest', s)} title="Edit"><FiEdit /></button>
                          <button className="btn-icon btn-danger" onClick={() => handleDelete('quest', s.id)} title="Delete"><FiTrash2 /></button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}


        {activeTab === 'survival' && (
          <section className="admin-section">
            <div className="section-header">
              <h2>Survival Kit</h2>
            </div>
            <div className="survival-two-col">
              <div className="survival-config-card">
                <h3>Currency &amp; Weather (single config)</h3>
                {survivalConfig && (
                  <ul className="config-preview">
                    <li>{survivalConfig.currency_from} → {survivalConfig.currency_to} (rate: {survivalConfig.rate})</li>
                    <li>Fun equivalent: {survivalConfig.fun_equivalent_label} @ {survivalConfig.fun_equivalent_unit_price}</li>
                    <li>Weather: {survivalConfig.weather_city} – {survivalConfig.weather_hint}</li>
                  </ul>
                )}
                <button className="btn-primary" onClick={openSurvivalEdit}>Edit Survival Config</button>
              </div>
              <div className="packing-list-card">
                <div className="section-header">
                  <h3>Packing Checklist</h3>
                  <button className="btn-primary" onClick={() => openCreate('packing')}><FiPlus /> Add Item</button>
                </div>
                <ul className="packing-list">
                  {packingItems.length === 0 ? (
                    <li className="empty-state">No packing items.</li>
                  ) : (
                    packingItems.map((i) => (
                      <li key={i.id}>
                        <span>{i.label}</span>
                        <span>
                          <button className="btn-icon" onClick={() => openEdit('packing', i)}><FiEdit /></button>
                          <button className="btn-icon btn-danger" onClick={() => handleDelete('packing', i.id)}><FiTrash2 /></button>
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'opportunities' && (
          <section className="admin-section">
            <div className="section-header">
              <h2>Special Opportunities</h2>
              <button className="btn-primary" onClick={() => openCreate('opportunity')}><FiPlus /> Add Opportunity</button>
            </div>
            <p className="student-app-admin-intro" style={{ marginBottom: 12 }}>
              Add scholarships, tuition discounts, special programs, and events. These appear in the mobile app for all students to see.
            </p>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Order</th><th>Title</th><th>Category</th><th>University</th><th>Deadline</th><th>Active</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {specialOpportunities.length === 0 ? (
                    <tr><td colSpan={7} className="empty-state">No special opportunities yet. Add one to show in the app.</td></tr>
                  ) : (
                    specialOpportunities.map((opp) => (
                      <tr key={opp.id}>
                        <td>{opp.order}</td>
                        <td>{opp.title}</td>
                        <td>{OPPORTUNITY_CATEGORY_LABELS[opp.category] || opp.category}</td>
                        <td>{opp.university || '—'}</td>
                        <td>{opp.deadline || '—'}</td>
                        <td>{opp.is_active ? 'Yes' : 'No'}</td>
                        <td>
                          <button className="btn-icon" onClick={() => openEdit('opportunity', opp)}><FiEdit /></button>
                          <button className="btn-icon btn-danger" onClick={() => handleDelete('opportunity', opp.id)}><FiTrash2 /></button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === 'costliving' && (
          <section className="admin-section">
            <div className="section-header">
              <h2>Cost of Living Calculator</h2>
            </div>
            <p className="student-app-admin-intro" style={{ marginBottom: 16 }}>
              Manage cities, categories, items, and monthly prices (in TRY) for the student cost of living calculator. Prices appear in the mobile app and can be updated anytime to reflect current costs.
            </p>

            {/* Currency Rate */}
            <div style={{
              background: 'linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)',
              border: '1px solid #bfdbfe', borderRadius: 12, padding: '20px 24px',
              marginBottom: 28,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <span style={{ fontSize: 18, marginRight: 8 }}>💱</span>
                  <strong style={{ fontSize: 15 }}>Currency Exchange Rate</strong>
                  <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280' }}>
                    Used to convert all TRY prices shown in the app
                  </span>
                </div>
                {!currencyEdit && (
                  <button className="btn-primary" style={{ fontSize: 13, padding: '6px 16px' }} onClick={handleCurrencyEdit}>
                    ✏️ Edit Rate
                  </button>
                )}
              </div>

              {!currencyEdit ? (
                <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>From</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1d4ed8' }}>{survivalConfig?.currency_from || '—'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: 20, color: '#9ca3af' }}>→</div>
                  <div>
                    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>To</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#1d4ed8' }}>{survivalConfig?.currency_to || '—'}</div>
                  </div>
                  <div style={{ marginLeft: 16 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>Rate (1 {survivalConfig?.currency_from || 'USD'} =)</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#15803d' }}>
                      {survivalConfig?.rate ? Number(survivalConfig.rate).toLocaleString() : '—'} {survivalConfig?.currency_to || 'TRY'}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>From</label>
                    <input
                      value={currencyEdit.currency_from}
                      onChange={e => setCurrencyEdit(p => ({ ...p, currency_from: e.target.value.toUpperCase() }))}
                      style={{ width: 80, padding: '8px 12px', borderRadius: 6, border: '1px solid #93c5fd', fontSize: 15, fontWeight: 700, textAlign: 'center' }}
                      maxLength={5}
                    />
                  </div>
                  <div style={{ paddingBottom: 10, fontSize: 18, color: '#9ca3af' }}>→</div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>To</label>
                    <input
                      value={currencyEdit.currency_to}
                      onChange={e => setCurrencyEdit(p => ({ ...p, currency_to: e.target.value.toUpperCase() }))}
                      style={{ width: 80, padding: '8px 12px', borderRadius: 6, border: '1px solid #93c5fd', fontSize: 15, fontWeight: 700, textAlign: 'center' }}
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>Rate (1 {currencyEdit.currency_from} =)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={currencyEdit.rate}
                      onChange={e => setCurrencyEdit(p => ({ ...p, rate: e.target.value }))}
                      style={{ width: 120, padding: '8px 12px', borderRadius: 6, border: '1px solid #93c5fd', fontSize: 15, fontWeight: 700 }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn-primary" style={{ padding: '8px 20px' }} onClick={handleCurrencySave} disabled={currencySaving}>
                      {currencySaving ? 'Saving…' : '✓ Save'}
                    </button>
                    <button className="btn-secondary" style={{ padding: '8px 16px' }} onClick={() => setCurrencyEdit(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Cities */}
            <div style={{ marginBottom: 24 }}>
              <div className="section-header">
                <h3>Cities</h3>
                <button className="btn-primary" onClick={handleCostCityCreate}><FiPlus /> Add City</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {costCities.map(c => (
                  <span key={c.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 12px', borderRadius: 16,
                    background: c.is_active ? '#e0e7ff' : '#f3f4f6',
                    color: c.is_active ? '#3730a3' : '#9ca3af', fontSize: 13, fontWeight: 500,
                  }}>
                    {c.name}{c.country ? `, ${c.country}` : ''}
                    {/* Text pairing so status isn't conveyed by color alone (MS-4) */}
                    {!c.is_active && <span style={{ fontSize: 11, fontWeight: 700 }}>· Inactive</span>}
                    <button className="btn-icon btn-danger" style={{ padding: 2, minWidth: 'auto' }} onClick={() => handleCostCityDelete(c.id)}><FiTrash2 size={12} /></button>
                  </span>
                ))}
                {costCities.length === 0 && <span style={{ color: '#9ca3af', fontSize: 13 }}>No cities added yet.</span>}
              </div>
            </div>

            {/* Categories & Items */}
            <div style={{ marginBottom: 24 }}>
              <div className="section-header">
                <h3>Categories &amp; Items</h3>
                <button className="btn-primary" onClick={handleCostCategoryCreate}><FiPlus /> Add Category</button>
              </div>
              {costCategories.length === 0 && <p style={{ color: '#9ca3af', fontSize: 13 }}>No categories. Add categories like Accommodation, Transport, Food, etc.</p>}
              {costCategories.map(cat => (
                <div key={cat.id} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginTop: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong>{cat.emoji} {cat.name}</strong>
                      <span style={{ marginLeft: 12, fontSize: 12, color: '#6b7280' }}>
                        {cat.input_type === 'SINGLE_CHOICE' ? 'Pick one' : cat.input_type === 'TIER_SELECT' ? 'Tier select' : 'Frequency select'}
                        {' \u2014 '}{cat.question}
                      </span>
                    </div>
                    <div>
                      <button className="btn-icon" onClick={() => handleCostCategoryEdit(cat)}><FiEdit /></button>
                      <button className="btn-icon btn-danger" onClick={() => handleCostCategoryDelete(cat.id)}><FiTrash2 /></button>
                    </div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Options: {(cat.options_json || []).join(', ') || 'none'}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {costItems.filter(i => i.category === cat.id).map(item => (
                        <span key={item.id} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 12,
                          background: item.is_active ? '#dcfce7' : '#f3f4f6',
                          fontSize: 12,
                        }}>
                          {item.emoji} {item.name}
                          {!item.is_active && <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>· Inactive</span>}
                          <button className="btn-icon" style={{ padding: 1, minWidth: 'auto' }} onClick={() => handleCostItemEdit(item)}><FiEdit size={11} /></button>
                          <button className="btn-icon btn-danger" style={{ padding: 1, minWidth: 'auto' }} onClick={() => handleCostItemDelete(item.id)}><FiTrash2 size={11} /></button>
                        </span>
                      ))}
                      <button style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '3px 10px', borderRadius: 12, border: '1px dashed #9ca3af',
                        background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#6b7280',
                      }} onClick={() => handleCostItemCreate(cat.id)}>
                        <FiPlus size={11} /> Add item
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {costCities.length > 0 && costItems.length > 0 && (
              <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8, fontStyle: 'italic' }}>
                Click the edit icon on any item above to set its weekly prices per city.
              </p>
            )}
          </section>
        )}

        {activeTab === 'delegation' && isMobileContentAdmin() && (
          <section className="admin-section">
            <div className="section-header">
              <h2>Access Control — Delegation</h2>
            </div>
            <p className="student-app-admin-intro" style={{ marginBottom: 16 }}>
              By default only Chairman, CEO, COO, and CMO can manage the mobile app content. Use this panel to grant access to specific team members on a case-by-case basis.
            </p>

            <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
              <select
                value={delegationUserId}
                onChange={e => setDelegationUserId(e.target.value)}
                style={{ flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db' }}
              >
                <option value="">Select team member to grant access...</option>
                {teamMembers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} ({u.role})
                  </option>
                ))}
              </select>
              <button className="btn-primary" onClick={handleAddDelegation} disabled={!delegationUserId}>
                <FiUserCheck /> Grant Access
              </button>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>Team Member</th><th>Role</th><th>Granted By</th><th>Date</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {delegations.length === 0 ? (
                    <tr><td colSpan={6} className="empty-state">No delegations yet. Only Chairman, CEO, COO, and CMO have access by default.</td></tr>
                  ) : (
                    delegations.map((d) => (
                      <tr key={d.id}>
                        <td>{d.delegated_user_name}</td>
                        <td>{d.delegated_user_role}</td>
                        <td>{d.delegated_by_name}</td>
                        <td>{new Date(d.created_at).toLocaleDateString()}</td>
                        <td>
                          <span style={{
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 12,
                            fontWeight: 600,
                            background: d.is_active ? '#dcfce7' : '#fee2e2',
                            color: d.is_active ? '#166534' : '#991b1b',
                          }}>
                            {d.is_active ? 'Active' : 'Revoked'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-icon"
                            title={d.is_active ? 'Revoke access' : 'Re-enable access'}
                            onClick={() => handleToggleDelegation(d.id, d.is_active)}
                          >
                            {d.is_active ? <FiUserX /> : <FiUserCheck />}
                          </button>
                          <button className="btn-icon btn-danger" onClick={() => handleRemoveDelegation(d.id)}>
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className={`modal-box student-app-modal${modalType === 'cost_item' && editingId ? ' wide-modal' : ''}`} onClick={e => e.stopPropagation()}>
            <h3>
              {modalType === 'quest' && (editingId ? 'Edit Stage' : 'Add Stage')}
              {modalType === 'packing' && (editingId ? 'Edit Packing Item' : 'Add Packing Item')}
              {modalType === 'opportunity' && (editingId ? 'Edit Opportunity' : 'Add Opportunity')}
              {modalType === 'cost_category' && (editingId ? 'Edit Category' : 'Add Category')}
              {modalType === 'cost_item' && (editingId ? 'Edit Item' : 'Add Item')}
              {modalType === 'survival' && 'Edit Survival Config'}
            </h3>
            <form onSubmit={handleSubmit}>
              {modalType === 'quest' && (
                <>
                  <label>Order <input type="number" value={form.order ?? ''} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))} /></label>
                  <label>Label <input type="text" value={form.label ?? ''} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required /></label>
                  <label>Emoji <input type="text" value={form.emoji ?? ''} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} /></label>
                  <label>Badge <input type="text" value={form.badge ?? ''} onChange={e => setForm(f => ({ ...f, badge: e.target.value }))} /></label>
                  <label><input type="checkbox" checked={!!form.is_milestone} onChange={e => setForm(f => ({ ...f, is_milestone: e.target.checked }))} /> Milestone (e.g. Admission)</label>
                </>
              )}
              {modalType === 'packing' && (
                <>
                  <label>Label <input type="text" value={form.label ?? ''} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} required /></label>
                  <label>Order <input type="number" value={form.order ?? ''} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))} /></label>
                  <label>Destination hint <input type="text" value={form.destination_hint ?? ''} onChange={e => setForm(f => ({ ...f, destination_hint: e.target.value }))} /></label>
                  <label><input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active</label>
                </>
              )}
              {modalType === 'opportunity' && (
                <>
                  <label>Title <input type="text" value={form.title ?? ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required /></label>
                  <label>Description <textarea value={form.description ?? ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} required /></label>
                  <label>Category
                    <select value={form.category ?? 'SCHOLARSHIP'} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                      {OPPORTUNITY_CATEGORIES.map((c) => <option key={c} value={c}>{OPPORTUNITY_CATEGORY_LABELS[c]}</option>)}
                    </select>
                  </label>
                  <label>University <input type="text" value={form.university ?? ''} onChange={e => setForm(f => ({ ...f, university: e.target.value }))} placeholder="e.g. Istanbul University" /></label>
                  <label>Country <input type="text" value={form.country ?? ''} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} /></label>
                  <label>Deadline <input type="date" value={form.deadline ?? ''} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} /></label>
                  <label>Link (external) <input type="url" value={form.link ?? ''} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} placeholder="https://..." /></label>
                  <label>Image URL
                    <input type="url" value={form.image_url ?? ''} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} onBlur={e => setForm(f => ({ ...f, image_url: directImageUrl(e.target.value) }))} placeholder="https://... or a Google Drive share link" />
                    <span style={{ display: 'block', fontSize: 12, color: '#64748b', marginTop: 4 }}>
                      Google Drive links work — share the file as &quot;Anyone with the link&quot;.
                    </span>
                    <ImagePreview url={form.image_url} />
                  </label>
                  <label>Order <input type="number" value={form.order ?? ''} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))} /></label>
                  <label><input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active (show in app)</label>
                </>
              )}
              {modalType === 'cost_category' && (
                <>
                  <label>Name <input type="text" value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Accommodation" /></label>
                  <label>Question <input type="text" value={form.question ?? ''} onChange={e => setForm(f => ({ ...f, question: e.target.value }))} required placeholder='e.g. "What type of accommodation?"' /></label>
                  <label>Emoji <input type="text" value={form.emoji ?? ''} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} /></label>
                  <label>Order <input type="number" value={form.order ?? ''} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))} /></label>
                  <label>Input type
                    <select value={form.input_type ?? 'TIER_SELECT'} onChange={e => setForm(f => ({ ...f, input_type: e.target.value }))}>
                      <option value="SINGLE_CHOICE">Pick one item</option>
                      <option value="TIER_SELECT">Select tier for each item</option>
                      <option value="FREQUENCY_SELECT">Select frequency for each item</option>
                    </select>
                  </label>
                  <label>Options (JSON array) <input type="text" value={form.options_json ?? '[]'} onChange={e => setForm(f => ({ ...f, options_json: e.target.value }))} placeholder='["None","Cheap","Moderate","Expensive"]' /></label>
                  <label><input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active</label>
                </>
              )}
              {modalType === 'cost_item' && (
                <>
                  <label>Name <input type="text" value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Metro/Bus" /></label>
                  <label>Emoji <input type="text" value={form.emoji ?? ''} onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} /></label>
                  <label>Order <input type="number" value={form.order ?? ''} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value, 10) || 0 }))} /></label>
                  <label><input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} /> Active</label>
                  {editingId && costCities.filter(c => c.is_active).length > 0 && (
                    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Monthly Prices (TRY)</h4>
                      {form._isSingleChoice ? (
                        // SINGLE_CHOICE: one price per city — simple two-column list
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {costCities.filter(c => c.is_active).map(city => (
                            <div key={city.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={{ width: 100, fontWeight: 500, fontSize: 13 }}>{city.name}</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form[`price_${city.id}_default`] ?? ''}
                                onChange={e => setForm(f => ({ ...f, [`price_${city.id}_default`]: e.target.value }))}
                                style={{ width: 120, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                                placeholder="0.00"
                              />
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>TRY / month</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // TIER_SELECT / FREQUENCY_SELECT: grid of cities × tiers
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                              <tr>
                                <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>City</th>
                                {(form._tiers || []).map(tier => (
                                  <th key={tier} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{tier}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {costCities.filter(c => c.is_active).map(city => (
                                <tr key={city.id}>
                                  <td style={{ padding: '4px 8px', fontWeight: 500, borderBottom: '1px solid #f3f4f6' }}>{city.name}</td>
                                  {(form._tiers || []).map(tier => (
                                    <td key={tier} style={{ padding: '2px 4px', borderBottom: '1px solid #f3f4f6' }}>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={form[`price_${city.id}_${tier}`] ?? ''}
                                        onChange={e => setForm(f => ({ ...f, [`price_${city.id}_${tier}`]: e.target.value }))}
                                        style={{ width: 80, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }}
                                        placeholder="0.00"
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {modalType === 'survival' && (
                <>
                  <label>Currency from <input type="text" value={form.currency_from ?? ''} onChange={e => setForm(f => ({ ...f, currency_from: e.target.value }))} /></label>
                  <label>Currency to <input type="text" value={form.currency_to ?? ''} onChange={e => setForm(f => ({ ...f, currency_to: e.target.value }))} /></label>
                  <label>Rate <input type="number" step="any" value={form.rate ?? ''} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} /></label>
                  <label>Fun equivalent label <input type="text" value={form.fun_equivalent_label ?? ''} onChange={e => setForm(f => ({ ...f, fun_equivalent_label: e.target.value }))} /></label>
                  <label>Fun equivalent unit price <input type="number" step="any" value={form.fun_equivalent_unit_price ?? ''} onChange={e => setForm(f => ({ ...f, fun_equivalent_unit_price: e.target.value }))} /></label>
                  <label>Weather city <input type="text" value={form.weather_city ?? ''} onChange={e => setForm(f => ({ ...f, weather_city: e.target.value }))} /></label>
                  <label>Weather hint <input type="text" value={form.weather_hint ?? ''} onChange={e => setForm(f => ({ ...f, weather_hint: e.target.value }))} /></label>
                </>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAppAdmin;
